import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';
import { saveSession, isLoggedIn, getRole } from '../services/auth';
import GoogleButton from '../components/GoogleButton';

const COUNTRY_CODES = [
  { flag: '🇮🇳', code: '91',  name: 'India' },
  { flag: '🇦🇺', code: '61',  name: 'Australia' },
  { flag: '🇬🇧', code: '44',  name: 'UK' },
  { flag: '🇺🇸', code: '1',   name: 'USA' },
  { flag: '🇨🇦', code: '1',   name: 'Canada' },
  { flag: '🇳🇿', code: '64',  name: 'New Zealand' },
  { flag: '🇩🇪', code: '49',  name: 'Germany' },
  { flag: '🇦🇪', code: '971', name: 'UAE' },
  { flag: '🇸🇬', code: '65',  name: 'Singapore' },
  { flag: '🇲🇾', code: '60',  name: 'Malaysia' },
];

function EyeIcon({ show }) {
  return show
    ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
    : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>;
}


export default function LoginPage() {
  const navigate = useNavigate();
  const [mode, setMode]         = useState('login');
  const [form, setForm]         = useState({ email: '', password: '' });
  const [showPass, setShowPass] = useState(false);
  const [showFpPass, setShowFpPass]   = useState(false);
  const [showFpPass2, setShowFpPass2] = useState(false);
  const [error, setError]       = useState('');
  const [info, setInfo]         = useState('');
  const [loading, setLoading]   = useState(false);
  const [fpPhone, setFpPhone]   = useState('');
  const [fpDialCode, setFpDialCode] = useState('91');
  const [fpOtp, setFpOtp]       = useState('');
  const [fpPass, setFpPass]     = useState('');
  const [fpPass2, setFpPass2]   = useState('');
  const location = useLocation();
  const [gCred, setGCred]       = useState('');
  const [gUser, setGUser]       = useState(null);   // { name, email } for new Google sign-ups
  const [gPhone, setGPhone]     = useState('');
  const [gDialCode, setGDialCode] = useState('91');
  const [gOtp, setGOtp]         = useState('');
  const [gAgree, setGAgree]     = useState(false);

  useEffect(() => {
    if (isLoggedIn()) {
      const role = getRole();
      if (role === 'admin') navigate('/admin');
      else if (role === 'councillor') navigate('/view');
      else navigate('/dashboard');
    }
  }, [navigate]);

  const goToHome = student => {
    const role = student?.role;
    if (role === 'admin') navigate('/admin');
    else if (role === 'councillor') navigate('/view');
    else navigate('/dashboard');
  };

  // ── Google sign-in ──────────────────────────────────────────────────────────
  const handleGoogle = async credential => {
    setError(''); setInfo(''); setLoading(true);
    try {
      const { data } = await api.post('/referral/auth/google', { credential });
      if (data.needs_phone) {
        setGCred(credential); setGUser({ name: data.name, email: data.email }); setMode('google_phone');
      } else {
        saveSession(data.token, data.student); goToHome(data.student);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Google sign-in failed');
    } finally { setLoading(false); }
  };

  // Google credential handed over from the Register page
  useEffect(() => {
    const cred = location.state?.googleCredential;
    if (cred) { navigate(location.pathname, { replace: true, state: null }); handleGoogle(cred); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const gFullPhone = () => gDialCode + gPhone.replace(/\D/g, '').replace(new RegExp('^' + gDialCode), '');

  const handleGoogleSendOtp = async e => {
    e.preventDefault(); setError(''); setInfo('');
    if (!gPhone) { setError('Please enter your WhatsApp number.'); return; }
    if (!gAgree) { setError('You must agree to the Terms & Conditions and referral reward policy.'); return; }
    setLoading(true);
    try {
      const { data } = await api.post('/referral/auth/send-otp', { phone: gFullPhone() });
      setInfo(data.message); setMode('google_otp');
    } catch (err) {
      if (err.response?.status === 202) { setInfo(err.response.data.detail); setMode('google_otp'); }
      else setError(err.response?.data?.detail || 'Failed to send OTP. Please try again.');
    } finally { setLoading(false); }
  };

  const handleGoogleComplete = async e => {
    e.preventDefault(); setError('');
    if (gOtp.length < 6) { setError('Please enter the complete 6-digit OTP.'); return; }
    setLoading(true);
    try {
      const { data } = await api.post('/referral/auth/google', { credential: gCred, phone: gFullPhone(), otp: gOtp });
      saveSession(data.token, data.student); goToHome(data.student);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create account.');
    } finally { setLoading(false); }
  };

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault(); setError(''); setLoading(true);
    try {
      const { data } = await api.post('/referral/auth/login', form);
      saveSession(data.token, data.student);
      goToHome(data.student);
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    } finally { setLoading(false); }
  };

  const handleForgotSend = async e => {
    e.preventDefault(); setError(''); setInfo('');
    if (!fpPhone) { setError('Please enter your WhatsApp number.'); return; }
    setLoading(true);
    const fullPhone = fpDialCode + fpPhone.replace(/\D/g, '').replace(new RegExp('^' + fpDialCode), '');
    try {
      const { data } = await api.post('/referral/auth/forgot-password', { phone: fullPhone });
      setInfo(data.message); setMode('forgot_otp');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send OTP. Please try again.');
    } finally { setLoading(false); }
  };

  const handleForgotVerify = async e => {
    e.preventDefault(); setError(''); setInfo('');
    if (fpOtp.length < 6) { setError('Please enter the complete 6-digit OTP.'); return; }
    setMode('forgot_newpass');
  };

  const handleForgotReset = async e => {
    e.preventDefault(); setError('');
    if (fpPass !== fpPass2) { setError('Passwords do not match.'); return; }
    if (fpPass.length < 6)  { setError('Password must be at least 6 characters.'); return; }
    setLoading(true);
    const fullPhone = fpDialCode + fpPhone.replace(/\D/g, '').replace(new RegExp('^' + fpDialCode), '');
    try {
      const { data } = await api.post('/referral/auth/reset-password', { phone: fullPhone, otp: fpOtp, password: fpPass });
      setInfo(data.message); setMode('login');
      setFpPhone(''); setFpOtp(''); setFpPass(''); setFpPass2('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password.');
    } finally { setLoading(false); }
  };

  const resetToLogin = () => { setMode('login'); setError(''); setInfo(''); setGCred(''); setGUser(null); setGPhone(''); setGOtp(''); setGAgree(false); setFpPhone(''); setFpDialCode('91'); setFpOtp(''); setFpPass(''); setFpPass2(''); };

  return (
    <div style={S.page}>
      {/* ── Centered Login Form ── */}
      <div style={S.right}>
        <div style={S.formBox}>

          {/* LOGIN */}
          {mode === 'login' && (
            <>
              <div style={S.formHeader}>
                <div style={S.formTitle}>Welcome Back 👋</div>
                <div style={S.formSub}>Sign in to your referral account</div>
              </div>
              <form onSubmit={handleSubmit}>
                <div style={S.fieldGroup}>
                  <label style={S.label}>Email Address</label>
                  <input name="email" type="email" value={form.email} onChange={handleChange}
                    required placeholder="you@example.com" style={S.input} />
                </div>
                <div style={S.fieldGroup}>
                  <label style={S.label}>Password</label>
                  <div style={S.eyeWrap}>
                    <input name="password" type={showPass ? 'text' : 'password'} value={form.password}
                      onChange={handleChange} required placeholder="Your password"
                      style={{ ...S.input, paddingRight: '2.5rem' }} />
                    <button type="button" style={S.eyeBtn} onClick={() => setShowPass(v => !v)} tabIndex={-1}>
                      <EyeIcon show={showPass} />
                    </button>
                  </div>
                </div>
                <div style={{ textAlign: 'right', marginBottom: '1.25rem' }}>
                  <button type="button" style={S.linkBtn} onClick={() => { setMode('forgot_email'); setError(''); setInfo(''); }}>
                    Forgot Password?
                  </button>
                </div>
                {info  && <div style={S.infoBox}>{info}</div>}
                {error && <div style={S.errorBox}>{error}</div>}
                <button type="submit" style={S.submitBtn} disabled={loading}>
                  {loading ? 'Signing in…' : 'Login →'}
                </button>
              </form>
              <GoogleButton onCredential={handleGoogle} />
              <p style={S.switchText}>New here? <Link to="/register" style={S.switchLink}>Create Account</Link></p>
            </>
          )}

          {/* GOOGLE — new account: phone */}
          {mode === 'google_phone' && (
            <>
              <div style={S.formHeader}>
                <div style={S.formTitle}>Almost there 🎉</div>
                <div style={S.formSub}>Hi {gUser?.name} ({gUser?.email}). Add your WhatsApp number to finish creating your account.</div>
              </div>
              <form onSubmit={handleGoogleSendOtp}>
                <div style={S.fieldGroup}>
                  <label style={S.label}>WhatsApp Phone Number</label>
                  <div style={{ display: 'flex', border: '1.5px solid #e2e8f0', borderRadius: 10, overflow: 'hidden', background: '#f8fafc' }}>
                    <select value={gDialCode} onChange={e => setGDialCode(e.target.value)}
                      style={{ padding: '.7rem .6rem', background: '#f1f5f9', border: 'none', borderRight: '1.5px solid #e2e8f0', fontSize: '.88rem', color: '#374151', fontWeight: 600, cursor: 'pointer', outline: 'none', minWidth: 130 }}>
                      {COUNTRY_CODES.map(c => (
                        <option key={c.flag + c.code + c.name} value={c.code}>{c.flag} +{c.code} {c.name}</option>
                      ))}
                    </select>
                    <input type="tel" value={gPhone} onChange={e => setGPhone(e.target.value.replace(/\D/g, ''))}
                      required placeholder="9876543210" autoFocus
                      style={{ flex: 1, padding: '.7rem .9rem', border: 'none', outline: 'none', fontSize: '.95rem', color: '#1e293b', background: 'transparent' }} />
                  </div>
                </div>
                <label style={{ display: 'flex', gap: '.5rem', alignItems: 'flex-start', fontSize: '.85rem', color: '#374151', marginBottom: '1rem', cursor: 'pointer' }}>
                  <input type="checkbox" checked={gAgree} onChange={e => setGAgree(e.target.checked)} style={{ marginTop: '.2rem' }} />
                  <span>I agree to the Terms &amp; Conditions and referral reward policy, and understand rewards are subject to verification, visa approval, and enrollment.</span>
                </label>
                {error && <div style={S.errorBox}>{error}</div>}
                <button type="submit" style={S.submitBtn} disabled={loading}>
                  {loading ? 'Sending OTP…' : 'Send OTP via WhatsApp'}
                </button>
              </form>
              <p style={S.switchText}><button style={S.linkBtn} onClick={resetToLogin}>Cancel</button></p>
            </>
          )}

          {/* GOOGLE — new account: OTP */}
          {mode === 'google_otp' && (
            <>
              <div style={S.formHeader}>
                <div style={S.formTitle}>Enter OTP 📲</div>
                <div style={S.formSub}>{info || `OTP sent to WhatsApp +${gFullPhone()}`}</div>
              </div>
              <form onSubmit={handleGoogleComplete}>
                <div style={S.fieldGroup}>
                  <label style={S.label}>6-Digit OTP</label>
                  <input type="text" inputMode="numeric" maxLength={6} value={gOtp}
                    onChange={e => setGOtp(e.target.value.replace(/\D/g, ''))}
                    required placeholder="123456" style={{ ...S.input, letterSpacing: '.35em', fontSize: '1.3rem', textAlign: 'center' }} autoFocus />
                </div>
                {error && <div style={S.errorBox}>{error}</div>}
                <button type="submit" style={S.submitBtn} disabled={loading}>
                  {loading ? 'Creating account…' : 'Verify & Create Account'}
                </button>
              </form>
              <p style={S.switchText}>
                <button style={S.linkBtn} onClick={() => { setMode('google_phone'); setGOtp(''); setError(''); }}>Change number / Resend</button>
                &nbsp;·&nbsp;
                <button style={S.linkBtn} onClick={resetToLogin}>Cancel</button>
              </p>
            </>
          )}

          {/* FORGOT — STEP 1 */}
          {mode === 'forgot_email' && (
            <>
              <div style={S.formHeader}>
                <div style={S.formTitle}>Reset Password 🔐</div>
                <div style={S.formSub}>Enter your WhatsApp number to receive an OTP</div>
              </div>
              <form onSubmit={handleForgotSend}>
                <div style={S.fieldGroup}>
                  <label style={S.label}>WhatsApp Phone Number</label>
                  <div style={{ display: 'flex', border: '1.5px solid #e2e8f0', borderRadius: 10, overflow: 'hidden', background: '#f8fafc' }}>
                    <select
                      value={fpDialCode}
                      onChange={e => setFpDialCode(e.target.value)}
                      style={{ padding: '.7rem .6rem', background: '#f1f5f9', border: 'none', borderRight: '1.5px solid #e2e8f0', fontSize: '.88rem', color: '#374151', fontWeight: 600, cursor: 'pointer', outline: 'none', minWidth: 130 }}
                    >
                      {COUNTRY_CODES.map(c => (
                        <option key={c.flag + c.code + c.name} value={c.code}>
                          {c.flag} +{c.code} {c.name}
                        </option>
                      ))}
                    </select>
                    <input
                      type="tel"
                      value={fpPhone}
                      onChange={e => setFpPhone(e.target.value.replace(/\D/g, ''))}
                      required
                      placeholder="9876543210"
                      style={{ flex: 1, padding: '.7rem .9rem', border: 'none', outline: 'none', fontSize: '.95rem', color: '#1e293b', background: 'transparent' }}
                      autoFocus
                    />
                  </div>
                </div>
                {error && <div style={S.errorBox}>{error}</div>}
                <button type="submit" style={S.submitBtn} disabled={loading}>
                  {loading ? 'Sending OTP…' : 'Send OTP via WhatsApp'}
                </button>
              </form>
              <p style={S.switchText}><button style={S.linkBtn} onClick={resetToLogin}>← Back to Login</button></p>
            </>
          )}

          {/* FORGOT — STEP 2 */}
          {mode === 'forgot_otp' && (
            <>
              <div style={S.formHeader}>
                <div style={S.formTitle}>Enter OTP 📲</div>
                <div style={S.formSub}>{info || `OTP sent to WhatsApp +${fpPhone}`}</div>
              </div>
              <form onSubmit={handleForgotVerify}>
                <div style={S.fieldGroup}>
                  <label style={S.label}>6-Digit OTP</label>
                  <input type="text" inputMode="numeric" maxLength={6} value={fpOtp}
                    onChange={e => setFpOtp(e.target.value.replace(/\D/g, ''))}
                    required placeholder="123456" style={{ ...S.input, letterSpacing: '.35em', fontSize: '1.3rem', textAlign: 'center' }} autoFocus />
                </div>
                {error && <div style={S.errorBox}>{error}</div>}
                <button type="submit" style={S.submitBtn} disabled={loading}>
                  {loading ? 'Verifying…' : 'Verify OTP'}
                </button>
              </form>
              <p style={S.switchText}>
                <button style={S.linkBtn} onClick={() => setMode('forgot_email')}>Resend OTP</button>
                &nbsp;·&nbsp;
                <button style={S.linkBtn} onClick={resetToLogin}>Cancel</button>
              </p>
            </>
          )}

          {/* FORGOT — STEP 3 */}
          {mode === 'forgot_newpass' && (
            <>
              <div style={S.formHeader}>
                <div style={S.formTitle}>New Password 🔑</div>
                <div style={S.formSub}>OTP verified. Set your new password below.</div>
              </div>
              <form onSubmit={handleForgotReset}>
                <div style={S.fieldGroup}>
                  <label style={S.label}>New Password</label>
                  <div style={S.eyeWrap}>
                    <input type={showFpPass ? 'text' : 'password'} value={fpPass}
                      onChange={e => setFpPass(e.target.value)} required placeholder="Min 6 characters"
                      style={{ ...S.input, paddingRight: '2.5rem' }} autoFocus />
                    <button type="button" style={S.eyeBtn} onClick={() => setShowFpPass(v => !v)} tabIndex={-1}><EyeIcon show={showFpPass} /></button>
                  </div>
                </div>
                <div style={S.fieldGroup}>
                  <label style={S.label}>Confirm Password</label>
                  <div style={S.eyeWrap}>
                    <input type={showFpPass2 ? 'text' : 'password'} value={fpPass2}
                      onChange={e => setFpPass2(e.target.value)} required placeholder="Re-enter password"
                      style={{ ...S.input, paddingRight: '2.5rem' }} />
                    <button type="button" style={S.eyeBtn} onClick={() => setShowFpPass2(v => !v)} tabIndex={-1}><EyeIcon show={showFpPass2} /></button>
                  </div>
                </div>
                {error && <div style={S.errorBox}>{error}</div>}
                <button type="submit" style={S.submitBtn} disabled={loading}>
                  {loading ? 'Saving…' : 'Set New Password'}
                </button>
              </form>
              <p style={S.switchText}><button style={S.linkBtn} onClick={resetToLogin}>Cancel</button></p>
            </>
          )}

        </div>
      </div>
    </div>
  );
}

/* ── Styles ── */
const S = {
  page: {
    display: 'flex', minHeight: '100vh', fontFamily: "'Inter', 'Segoe UI', sans-serif",
    alignItems: 'center', justifyContent: 'center', background: '#f8fafc',
  },
  loanBanner: {
    display: 'flex', alignItems: 'center', gap: '1rem',
    background: 'linear-gradient(135deg, #7c3aed, #2563eb, #0891b2)',
    borderRadius: 14, padding: '1rem 1.25rem', marginBottom: '1.75rem',
    boxShadow: '0 4px 20px rgba(124,58,237,0.35)',
  },
  loanIcon: { fontSize: '2.2rem', flexShrink: 0 },
  loanTitle: {
    fontSize: '1.05rem', fontWeight: 900, color: '#fff',
    marginBottom: '.2rem', letterSpacing: '.01em',
    textShadow: '0 1px 4px rgba(0,0,0,.2)',
  },
  loanSub: { fontSize: '.78rem', color: '#e0f2fe', lineHeight: 1.5 },
  noteIcon: { fontSize: '1rem', flexShrink: 0, marginTop: '.05rem' },
  contact: {
    display: 'flex', gap: '.6rem', alignItems: 'flex-start',
    background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.25)',
    borderRadius: 8, padding: '.7rem .9rem', fontSize: '.82rem', color: '#c7d2fe',
    lineHeight: 1.5, marginTop: '.6rem',
  },
  contactIcon: { fontSize: '1rem', flexShrink: 0 },
  contactLink: { color: '#a5b4fc', fontWeight: 700, textDecoration: 'none' },
  contactSep: { color: '#4b5563', margin: '0 .2rem' },

  right: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem', width: '100%',
  },
  formBox: {
    background: '#fff', borderRadius: 20, padding: '2.5rem',
    boxShadow: '0 8px 40px rgba(0,0,0,.10)', width: '100%', maxWidth: 420,
  },
  formHeader: { marginBottom: '1.75rem' },
  formTitle: { fontSize: '1.6rem', fontWeight: 800, color: '#0f172a', marginBottom: '.35rem' },
  formSub: { color: '#64748b', fontSize: '.9rem' },
  fieldGroup: { marginBottom: '1.1rem' },
  label: { display: 'block', fontSize: '.82rem', fontWeight: 600, color: '#374151', marginBottom: '.4rem' },
  input: {
    width: '100%', padding: '.7rem 1rem', border: '1.5px solid #e2e8f0', borderRadius: 10,
    fontSize: '.95rem', color: '#1e293b', outline: 'none', boxSizing: 'border-box',
    transition: 'border-color .15s', background: '#f8fafc',
  },
  eyeWrap: { position: 'relative', display: 'flex', alignItems: 'center' },
  eyeBtn: {
    position: 'absolute', right: '.75rem', background: 'none', border: 'none',
    cursor: 'pointer', color: '#64748b', display: 'flex', padding: 0,
  },
  submitBtn: {
    width: '100%', padding: '.85rem', background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
    color: '#fff', border: 'none', borderRadius: 10, fontSize: '1rem', fontWeight: 700,
    cursor: 'pointer', marginTop: '.25rem', marginBottom: '1rem', letterSpacing: '.03em',
    transition: 'opacity .15s',
  },
  linkBtn: {
    background: 'none', border: 'none', color: '#6366f1', cursor: 'pointer',
    fontSize: '.88rem', fontWeight: 600, padding: 0, textDecoration: 'underline',
  },
  switchText: { textAlign: 'center', fontSize: '.9rem', color: '#64748b', margin: 0 },
  switchLink: { color: '#6366f1', fontWeight: 700, textDecoration: 'none' },
  infoBox: {
    background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534',
    borderRadius: 8, padding: '.65rem .9rem', fontSize: '.88rem', marginBottom: '1rem',
  },
  errorBox: {
    background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626',
    borderRadius: 8, padding: '.65rem .9rem', fontSize: '.88rem', marginBottom: '1rem',
  },
};
